/**
 * ReviewComponents Component
 *
 * This component fetches and displays a list of reviews for a specific profile,
 * along with a pagination component for navigating through multiple pages of reviews.
 * It shows a loading state while fetching data and handles the display of default
 * profile images if the user's cover photo is unavailable.
 *
 * File Name: ReviewComponents.tsx
 * Date: 01-10-2024
 *
 * Marker: DOC_START
 */

import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import { IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import qs from "qs";
import defaultProfile from "src/assets/images/default_profile_image.png";
import ReviewLoader from "./ReviewLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { convertToTimeAgo } from "src/helpers/helperFunctions";
import PaginationComponet from "./GlobalComponents/PaginationComponet";
import FavStarIcon from "./GlobalComponents/FavStarIcon";
import { capitalize } from "@mui/material";
import { DummyReviewsData } from "src/lib/DummmyData";

interface IReviewItem {
  _id: string;
  comment: string;
  star: number;
  customerFirstName: string;
  customerlastName: string;
  customerProfileImageUrl: string;
  createdAt: string;
  like: string;
  longConsultation: string;
  diagnosis: string;
  doctorConsultation: string;
  updatedAt: string;
}

const ReviewComponents: React.FC<{ id: string; mainClass?: string }> = ({
  id = "",
  mainClass = "",
}) => {
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
  });
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination
  const [data, setData] = useState<IReviewItem[]>([]);
  // API_CALL: Fetch review from the serve
  const fetch = async () => {
    setIsLoader(true);
    const { res, err } = await httpRequest<{
      documents: IReviewItem[];
      paginated: IPaginated;
    }>({ path: `/review/get-profile/${id}?${qs.stringify(queryParams)}` });
    if (res) {
      let { documents = [], paginated } = res;
      setData(documents);
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  useEffect(() => {
    fetch();
  }, [id, queryParams]);

  const getStars = (likes: string) => {
    let likeCount = Number(likes);
    return Math.floor(likeCount / 2);
  };
  return (
    <>
      {isLoader ? (
        <ReviewLoader length={5} />
      ) : (
        data.map((item: IReviewItem, index: number) => (
          <div key={index} className={`px-4 py-4 ${mainClass}`}>
            <div className="reviews relative rounded-[15px] bg-[#FFFCF1] shadow-[0px_0px_10px_#00000029] w-full flex gap-2 items-center mt-4 px-4 py-6">
              <div className="flex items-center justify-center flex-col gap-1 px-2 lg:px-[0.4vw] w-[12%]">
                <img
                  src={`${item?.customerProfileImageUrl}`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target) {
                      target.src = defaultProfile;
                    }
                  }}
                  alt="profile-img"
                  className="rounded-full w-[65px] h-[65px] object-cover"
                />
                <h2 className="font-size-16px font-Poppins-Medium theme-color-green capitalize text-center line-clamp-2">
                  {`${item?.customerFirstName || ""} ${
                    item?.customerlastName || ""
                  }`}
                </h2>
                <p className="font-size-13px font-Poppins-Regular leading-none">
                  Patient
                </p>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <div className="flex w-full justify-between">
                  <FavStarIcon rating={getStars(item.like)} />
                  <div className="font-size-13px font-Poppins-Regular text-[#636363]">
                    {convertToTimeAgo(item.updatedAt)}
                  </div>
                </div>
                <p className="font-size-15px font-Poppins-Regular text-[#555555]">
                  {item?.comment || ""}
                </p>
                <div className="flex gap-2 items-center justify-start flex-wrap">
                  <div className="border border-[#22C55E] bg-[#F0FDF4] rounded-[5px] xl:rounded-[10px] p-2 px-2.5">
                    <p className="font-size-15px font-Poppins-Regular text-[#2E2E2E]">
                      <span className="text-[#555555] ">Waiting Time:</span>{" "}
                      <span className="font-Poppins-Medium">
                        {item.longConsultation}
                      </span>
                    </p>
                  </div>
                  <div className="border border-[#22C55E] rounded-[5px] bg-[#F0FDF4] xl:rounded-[10px] p-2 px-2.5">
                    <p className="font-size-15px font-Poppins-Regular text-[#2E2E2E]">
                      <span className="text-[#555555] ">
                        Consultation Time:
                      </span>{" "}
                      <span className="font-Poppins-Medium">
                        {item.doctorConsultation}
                      </span>
                    </p>
                  </div>
                  <div className="border border-[#22C55E] rounded-[5px] bg-[#F0FDF4] xl:rounded-[10px] p-2 px-2.5">
                    <p className="font-size-15px font-Poppins-Regular text-[#2E2E2E]">
                      <span className="text-[#555555] ">
                        Satisfied with Diagnosis:
                      </span>{" "}
                      <span className="font-Poppins-Medium">
                        {capitalize(item.diagnosis || "no")}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      )}

      <PaginationComponet
        currentPage={paginated.currentPage || 1}
        total={paginated.totalPages || 1}
        setCurrentPage={(_page) =>
          setQueryParams((prev) => ({ ...prev, page: _page }))
        }
      />
    </>
  );
};

export default ReviewComponents;
